import { useEffect, useState } from 'react';

import userService from './auth.service';

// **Τα μέλη μιας συλλογής, για τον επιλογέα αναφορών** (D44).
//
// ⚠ **ΓΙΑΤΙ ΝΩΘΡΗ ΑΝΑΚΤΗΣΗ ΚΑΙ ΟΧΙ PROP.** Ο επιλογέας χρειάζεται τα μέλη σε τρία
// σημεία, και τα δύο τα έχουν ήδη — ο διάλογος του καθήκοντος και η σελίδα της
// συλλογής κρατούν το έγγραφο με τα μέλη του. Το τρίτο, η στήλη των κρίσεων μέσα
// στο παράθυρο του πόρου, **δεν** τα έχει: ο μόνος γονέας της είναι η κάρτα του
// πόρου, που δεν της τα δίνει.
//
// Η ανάκτηση συμβαίνει **μόνο όταν ο χρήστης πληκτρολογήσει `@`**, μία φορά ανά
// συλλογή, και το αποτέλεσμα κρατιέται όσο ζει η σελίδα. Στη συνηθισμένη
// περίπτωση —σχόλιο χωρίς αναφορά— το κόστος είναι **μηδέν αιτήματα**. Είναι το
// πρότυπο της **D38**, με το ίδιο επιχείρημα: το πλαίσιο έρχεται όταν χρειαστεί,
// και όσο δεν έχει έρθει ο κατάλογος **δηλώνει αναμονή** αντί να είναι κενός —
// κενός κατάλογος διαβάζεται ως απάντηση «καμία επιλογή».
//
// **Μία διατύπωση για τις δύο περιπτώσεις.** Όποιος έχει ήδη τα μέλη τα περνά· ο
// συνδρομητής επιστρέφει εκείνα και δεν ρωτά κανέναν. Δύο δρόμοι στον καλούντα
// —«έχω μέλη» και «δεν έχω»— θα σήμαιναν ότι κάθε σημείο χρήσης ξαναγράφει την
// ίδια συνθήκη.

/**
 * Η μνήμη, **ανά συλλογή και όσο ζει η σελίδα**.
 *
 * Δεν είναι προσωρινή αποθήκη με λήξη: η ιδιότητα μέλους αλλάζει σπάνια, και μια
 * αλλαγή που δεν φάνηκε σημαίνει το πολύ ότι ο επιλογέας δεν προσφέρει ένα νέο
 * μέλος. Ο **φράχτης** δεν είναι εδώ — είναι το **422** του διακομιστή, που
 * κρίνει με τα σημερινά μέλη.
 */
const cache = new Map();
const inFlight = new Map();

/** Τα μέλη μιας συλλογής, από τη μνήμη ή από το API. */
export function loadMembers(collectionId) {
    if (cache.has(collectionId)) return Promise.resolve(cache.get(collectionId));
    if (inFlight.has(collectionId)) return inFlight.get(collectionId);

    const promise = userService
        .getCollection(collectionId)
        .then((collection) => {
            const members = (collection?.members ?? [])
                .map((member) => member.user)
                .filter(Boolean);
            cache.set(collectionId, members);
            return members;
        })
        .catch(() => {
            // **Η αποτυχία δεν απομνημονεύεται.** Ένα δίκτυο που έπεσε μια φορά
            // δεν επιτρέπεται να αφήσει τον επιλογέα άδειο για όσο μένει ανοιχτή
            // η σελίδα: η επόμενη πληκτρολόγηση `@` ξαναρωτά.
            return [];
        })
        .finally(() => inFlight.delete(collectionId));

    inFlight.set(collectionId, promise);
    return promise;
}

/**
 * Ο συνδρομητής του επιλογέα.
 *
 * @param collectionId Η συλλογή της ενέργειας.
 * @param provided     Τα μέλη, αν ο καλών τα έχει ήδη. Τότε δεν γίνεται αίτημα.
 * @param isActive     `true` όταν ο χρήστης έχει πληκτρολογήσει `@`. Η ανάκτηση
 *   ξεκινά **εδώ** και όχι στη φόρτωση: αλλιώς κάθε άνοιγμα πόρου θα πλήρωνε ένα
 *   αίτημα για δυνατότητα που οι περισσότεροι δεν θα χρησιμοποιήσουν.
 */
export function useMentionMembers(collectionId, { provided = null, isActive = false } = {}) {
    const [members, setMembers] = useState(provided ?? []);
    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        if (provided) {
            setMembers(provided);
            return undefined;
        }
        if (!isActive || !collectionId) return undefined;
        if (cache.has(collectionId)) {
            setMembers(cache.get(collectionId));
            return undefined;
        }

        let alive = true;
        setLoading(true);
        loadMembers(collectionId).then((found) => {
            if (!alive) return;
            setMembers(found);
            setLoading(false);
        });

        return () => {
            alive = false;
        };
    }, [collectionId, isActive, provided]);

    return { members, isLoadingMembers: isLoading };
}

export default { loadMembers, useMentionMembers };
